import { useEffect, useMemo, useState } from 'react';
import type { ChartDataset } from 'chart.js';
import { ExternalLink, RotateCcw, Search } from 'lucide-react';
import { ChartBox } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { RankList } from '../components/RankList';
import { Tag } from '../components/Tag';
import { localDevToken } from '../api';
import { num } from '../utils';
import { TARGET_COLOR, TARGET_PARENTS, parentOf } from '../targetGroups';

// Hỏi hàng cũ nội bộ (LARK "Hỏi đặt hàng CŨ"): sale ↔ team Dự án. Tự tải /api/care/secondhand-lark theo
// kỳ của màn #/secondhand; types nằm ở đây để khối này độc lập với phần hội thoại online và doanh số.

export interface LarkAsk {
  ask_id: string; root_message_id: string; item_no: number; day: string; asked_at: string;
  asker_name?: string | null; asker_email?: string | null; asker_store?: string | null; asker_team?: string | null;
  product_text?: string | null; model_text?: string | null; brand?: string | null;
  ai_category?: string | null; ai_subcategory?: string | null;
  ask_type: string; source_hint: string; availability: string; quoted_price?: number | null;
  outcome: string; blocker: string; responder_name?: string | null;
  first_reply_minutes?: number | null; reply_count: number;
  order_codes: string[]; sku_codes: string[];
  summary?: string | null; next_step?: string | null; confidence?: number | null; app_link?: string | null;
}
interface StaffSale { email: string; name?: string | null; used_orders: number; used_rev: number | null }
interface OrderMatch { code: string; d: string; sp?: string | null; rev: number | null; used_rev: number | null; used_lines: number; st?: string | null }
export type LarkRole = 'sales_question' | 'sales_followup' | 'project_reply' | 'project_update' | 'other';
export interface LarkRawMessage { root: string; id: string; is_root: boolean; role: LarkRole; name?: string | null; t: string; text: string; app_link?: string | null }
export interface LarkResponse {
  meta: { range?: string; refreshed_at?: string; last_message?: string | null; last_enriched?: string | null; pending_threads?: number };
  asks: LarkAsk[];
  raw?: LarkRawMessage[];
  sales: { by_staff: StaffSale[]; orders: OrderMatch[]; window?: { start: string; end: string }; error?: string | null };
}

async function loadLark(range: string): Promise<LarkResponse> {
  const token = localDevToken();
  const res = await fetch('/api/care/secondhand-lark?range=' + encodeURIComponent(range), {
    credentials: 'same-origin', headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(String(body.error || body.message || `HTTP ${res.status}`));
  return body as LarkResponse;
}

const AVAIL_LABEL: Record<string, string> = {
  available: 'Có hàng', alternative: 'Đề xuất mã khác', unavailable: 'Không có', sold: 'Seller đã bán', sourcing: 'Đang dò', unknown: 'Chưa trả lời',
};
const AVAIL_TONE: Record<string, 'green' | 'teal' | 'red' | 'amber' | 'blue' | 'gray'> = {
  available: 'green', alternative: 'teal', unavailable: 'red', sold: 'amber', sourcing: 'blue', unknown: 'gray',
};
const AVAIL_COLOR: Record<string, string> = {
  available: '#059669', alternative: '#06b6d4', sourcing: '#2563eb', sold: '#d97706', unavailable: '#dc2626', unknown: '#94a3b8',
};
const AVAIL_ORDER = ['available', 'alternative', 'sourcing', 'sold', 'unavailable', 'unknown'];
const OUTCOME_LABEL: Record<string, string> = { closed: 'Đã lên đơn', deposit: 'Đã cọc', pending: 'Đang chờ', lost: 'Không chốt', unknown: 'Chưa rõ' };
const OUTCOME_TONE: Record<string, 'green' | 'teal' | 'amber' | 'red' | 'gray'> = { closed: 'green', deposit: 'teal', pending: 'amber', lost: 'red', unknown: 'gray' };
const TYPE_LABEL: Record<string, string> = {
  find_used: 'Nhờ tìm hàng', check_listed: 'Hỏi mã trong list', trade_in_price: 'Hỏi giá thu cũ',
  warranty_info: 'Hỏi bảo hành', accessory: 'Phụ kiện kèm', price_feedback: 'Phản ánh giá', other: 'Khác',
};
const TEAM_LABEL: Record<string, string> = { online: 'KD Online', store: 'Cửa hàng', du_an: 'Dự án', bod: 'BOD', other: 'Khác' };
const MATCHED = new Set(['available', 'alternative']);
const LINK_BTN = { background: 'none', border: 0, padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', textDecoration: 'underline dotted' } as const;

// Role is decided by the sender, not by the model: sales ask, the project team (Dũng, Sanh, Tuấn) answers.
const ROLE_LABEL: Record<LarkRole, string> = {
  sales_question: 'Sales hỏi', sales_followup: 'Sales', project_reply: 'Dự án', project_update: 'Dự án cập nhật', other: 'Khác',
};
const ROLE_TONE: Record<LarkRole, 'blue' | 'gray' | 'violet' | 'teal'> = {
  sales_question: 'blue', sales_followup: 'gray', project_reply: 'violet', project_update: 'teal', other: 'gray',
};
const isProjectRole = (r: LarkRole) => r === 'project_reply' || r === 'project_update';

function RawThread({ msgs }: { msgs: LarkRawMessage[] }) {
  const [open, setOpen] = useState(false);
  if (!msgs.length) return <span className="muted">—</span>;
  // Collapsed: the question and the project team's answers; expanded: every message in order.
  const key = msgs.filter(m => m.is_root || isProjectRole(m.role));
  const shown = open ? msgs : key.slice(0, 4);
  const hidden = msgs.length - shown.length;
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 320, maxWidth: 560 }}>
      {shown.map(m => (
        <div key={m.id} style={{ lineHeight: 1.35 }}>
          <Tag tone={ROLE_TONE[m.role]}>{ROLE_LABEL[m.role]}</Tag>{' '}
          <span className="muted small">{shortName(m.name)} · {m.t.slice(8, 10)}/{m.t.slice(5, 7)} {m.t.slice(11, 16)}</span>
          <div className="small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
        </div>
      ))}
      {(hidden > 0 || open) && (
        <button type="button" style={LINK_BTN} className="small muted" onClick={() => setOpen(v => !v)}>
          {open ? 'Thu gọn' : `Xem cả thread (+${hidden} tin)`}
        </button>
      )}
    </div>
  );
}

type Dim = 'team' | 'store' | 'staff' | 'target' | 'model' | 'brand' | 'avail';
const DIM_LABEL: Record<Dim, string> = { team: 'Đội', store: 'Store', staff: 'Nhân viên', target: 'Ngành', model: 'Mã/dòng', brand: 'Hãng', avail: 'Trả lời' };
const valueOf = (a: LarkAsk, d: Dim): string => {
  if (d === 'team') return a.asker_team || 'other';
  if (d === 'store') return a.asker_store || '?';
  if (d === 'staff') return a.asker_name || '?';
  if (d === 'target') return parentOf(a);
  if (d === 'model') return a.model_text || a.product_text || '?';
  if (d === 'brand') return a.brand || 'Chưa rõ';
  return a.availability || 'unknown';
};
const labelOf = (d: Dim, v: string): string => {
  if (d === 'team') return TEAM_LABEL[v] || v;
  if (d === 'target') return TARGET_PARENTS.find(p => p.code === v)?.label || v;
  if (d === 'avail') return AVAIL_LABEL[v] || v;
  return v;
};
const shortName = (n?: string | null) => (n || '?').replace(/\s*-\s*(NV|CV|QL|TN)[A-Z]*\s*$/i, '').trim();
const mil = (n?: number | null) => (n ? `${(n / 1_000_000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} tr` : '—');
const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const hhmm = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Ho_Chi_Minh' });
};
function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}
function countBy(rows: LarkAsk[], fn: (a: LarkAsk) => string): Array<[string, number]> {
  const m = new Map<string, number>();
  rows.forEach(a => { const k = fn(a); if (k) m.set(k, (m.get(k) || 0) + 1); });
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

export function SecondhandLarkSection({ range, target = '' }: { range: string; target?: string }) {
  const [data, setData] = useState<LarkResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<Record<Dim, string[]>>({ team: [], store: [], staff: [], target: [], model: [], brand: [], avail: [] });
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(60);

  useEffect(() => {
    let alive = true;
    setBusy(true); setError('');
    loadLark(range).then(d => { if (alive) setData(d); }).catch(e => { if (alive) setError(String((e as Error).message || e)); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [range]);

  // Page-level target from the pinned bar narrows every block of this section.
  const asks = useMemo(() => (data?.asks || []).filter(a => !target || parentOf(a) === target), [data, target]);
  const rawByRoot = useMemo(() => {
    const m = new Map<string, LarkRawMessage[]>();
    (data?.raw || []).forEach(x => m.set(x.root, [...(m.get(x.root) || []), x]));
    return m;
  }, [data]);
  const toggle = (d: Dim, v: string) => setFilters(f => ({ ...f, [d]: f[d].includes(v) ? f[d].filter(x => x !== v) : [...f[d], v] }));
  const clear = () => { setFilters({ team: [], store: [], staff: [], target: [], model: [], brand: [], avail: [] }); setQuery(''); };
  const dims = Object.keys(filters) as Dim[];
  const active = dims.reduce((n, d) => n + filters[d].length, 0) + (query.trim() ? 1 : 0);
  const needle = query.trim().toLowerCase();

  const view = useMemo(() => {
    const base = needle
      ? asks.filter(a => `${a.product_text || ''} ${a.model_text || ''} ${a.brand || ''} ${a.summary || ''} ${a.asker_name || ''}`.toLowerCase().includes(needle))
      : asks;
    const apply = (except?: Dim) => base.filter(a => dims.every(d => d === except || !filters[d].length || filters[d].includes(valueOf(a, d))));
    return { all: apply(), staff: apply('staff'), target: apply('target'), model: apply('model'), brand: apply('brand'), avail: apply('avail'), team: apply('team') };
  }, [asks, filters, needle]);

  const rows = view.all;
  const threads = new Set(rows.map(a => a.root_message_id));
  const askers = new Set(rows.map(a => a.asker_name || '?'));
  const matched = rows.filter(a => MATCHED.has(a.availability)).length;
  const noStock = rows.filter(a => a.availability === 'unavailable' || a.availability === 'sold').length;
  const closedThreads = new Set(rows.filter(a => a.outcome === 'closed' || a.outcome === 'deposit').map(a => a.root_message_id));
  const firstReplies = [...new Map(rows.filter(a => a.first_reply_minutes != null).map(a => [a.root_message_id, Number(a.first_reply_minutes)])).values()];
  const med = median(firstReplies);
  const orders = data?.sales.orders || [];
  const codesInView = new Set(rows.flatMap(a => a.order_codes));
  const matchedOrders = orders.filter(o => codesInView.has(o.code));
  const matchedUsedRev = matchedOrders.reduce((s, o) => s + (o.used_rev || 0), 0);
  const matchedTotalRev = matchedOrders.reduce((s, o) => s + (o.rev || 0), 0);

  // Daily trend: items asked, stacked by the project team's answer.
  // Continuous day axis: a day with no ask is a zero, not a missing column.
  const seen = [...new Set(rows.map(a => a.day))].sort();
  const days: string[] = [];
  if (seen.length) {
    for (let d = new Date(`${seen[0]}T00:00:00Z`); d <= new Date(`${seen[seen.length - 1]}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
      days.push(d.toISOString().slice(0, 10));
    }
  }
  const byDayAvail = (day: string, av: string) => rows.filter(a => a.day === day && a.availability === av).length;
  const datasets: ChartDataset[] = [
    ...AVAIL_ORDER.map(av => ({
      label: AVAIL_LABEL[av], data: days.map(d => byDayAvail(d, av)), stack: 'a', backgroundColor: `${AVAIL_COLOR[av]}cc`,
      borderRadius: 3, maxBarThickness: 36, datalabels: { display: false },
    }) as unknown as ChartDataset),
    {
      label: 'Thread chốt/cọc', type: 'line', data: days.map(d => new Set(rows.filter(a => a.day === d && (a.outcome === 'closed' || a.outcome === 'deposit')).map(a => a.root_message_id)).size),
      borderColor: '#111827', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 3, tension: 0.3, datalabels: { display: false },
    } as unknown as ChartDataset,
  ];

  // Staff table: asks from LARK beside the same person's used-goods orders in f_sales (email key).
  const salesByEmail = new Map((data?.sales.by_staff || []).map(s => [s.email, s]));
  const staffRows = [...new Map(view.staff.map(a => [a.asker_name || '?', a])).keys()].map(name => {
    const mine = view.staff.filter(a => (a.asker_name || '?') === name);
    const email = mine.find(a => a.asker_email)?.asker_email || '';
    const s = salesByEmail.get(email.toLowerCase());
    return {
      name, email, team: mine[0]?.asker_team || 'other', store: mine[0]?.asker_store || '',
      asks: mine.length, threads: new Set(mine.map(a => a.root_message_id)).size,
      matched: mine.filter(a => MATCHED.has(a.availability)).length,
      closed: new Set(mine.filter(a => a.outcome === 'closed' || a.outcome === 'deposit').map(a => a.root_message_id)).size,
      usedOrders: s?.used_orders ?? null, usedRev: s?.used_rev ?? null,
    };
  }).sort((a, b) => b.asks - a.asks);

  // Project team: replies inside the threads of the asks in view, plus the updates they posted.
  const viewRoots = new Set(rows.map(a => a.root_message_id));
  const projectMsgs = (data?.raw || []).filter(m => m.role === 'project_reply' && viewRoots.has(m.root));
  const updates = (data?.raw || []).filter(m => m.role === 'project_update').sort((a, b) => b.t.localeCompare(a.t));
  const firstBy = new Map(rows.filter(a => a.responder_name).map(a => [a.root_message_id, { name: a.responder_name as string, min: a.first_reply_minutes }]));
  const teamRows = [...new Set([...projectMsgs.map(m => m.name || '?'), ...updates.map(m => m.name || '?')])].map(name => {
    const mine = projectMsgs.filter(m => (m.name || '?') === name);
    const firsts = [...firstBy.values()].filter(f => f.name === name);
    return {
      name, replies: mine.length, threads: new Set(mine.map(m => m.root)).size, first: firsts.length,
      med: median(firsts.filter(f => f.min != null).map(f => Number(f.min))),
      updates: updates.filter(m => (m.name || '?') === name).length,
    };
  }).sort((a, b) => b.replies - a.replies);

  const gapNote = (key: string) => {
    const n = view.model.filter(a => (a.model_text || a.product_text || '?') === key && (a.availability === 'unavailable' || a.availability === 'sold')).length;
    return n ? `${n} không có` : null;
  };
  const targetEntries = countBy(view.target, a => parentOf(a));
  const freshness = data?.meta.last_message ? `tin mới nhất ${ddmm(data.meta.last_message.slice(0, 10))} ${hhmm(data.meta.last_message)}` : '';

  const title = (
    <div className="section-title space">
      <h2 id="sh-lark" className="ins-anchor">Sale hỏi team Dự án hàng cũ (LARK)</h2>
      <span>Nhóm "Hỏi đặt hàng CŨ" · Local-AI tách từng mã được hỏi{freshness ? ` · ${freshness}` : ''}{data?.meta.pending_threads ? ` · ${data.meta.pending_threads} thread chờ AI` : ''}</span>
    </div>
  );
  if (busy && !data) return <>{title}<div className="card empty">Đang tải lượt hỏi hàng cũ trên LARK...</div></>;
  if (error && !data) return <>{title}<div className="card empty">Không tải được dữ liệu LARK: {error}</div></>;
  if (!asks.length) return <>{title}<div className="card empty">Chưa có lượt hỏi hàng cũ trên LARK trong kỳ này.</div></>;

  return (
    <>
      {title}
      <div className="card space">
        <div className="ins-row">
          <div className="ins-crumbs">
            <em>{num(rows.length)} / {num(asks.length)} mã được hỏi</em>
            {dims.flatMap(d => filters[d].map(v => (
              <button key={`${d}:${v}`} type="button" className="ins-chip" onClick={() => toggle(d, v)} title="Bỏ lọc này">
                <span>{DIM_LABEL[d]}:</span> {labelOf(d, v)} ×
              </button>
            )))}
          </div>
          <div className="ins-actions">
            <div className="ins-seg" role="group" aria-label="Đội">
              {['online', 'store'].map(t => (
                <button key={t} type="button" aria-pressed={filters.team.includes(t)} onClick={() => toggle('team', t)}>{TEAM_LABEL[t]}</button>
              ))}
            </div>
            <label className={`ins-search ${query ? 'has-value' : ''}`} title="Tìm theo sản phẩm, hãng, nhân viên, tóm tắt">
              <Search size={14} />
              <input type="search" value={query} placeholder="Tìm mã, hãng, nhân viên…" onChange={e => { setQuery(e.target.value); setLimit(60); }} />
            </label>
            <button type="button" className="ins-clear" onClick={clear} disabled={!active} title="Xóa lọc"><RotateCcw size={15} /><span>Xóa lọc</span></button>
          </div>
        </div>
      </div>

      <section className="grid kpis">
        <KpiCard label="Mã hàng cũ được hỏi" value={num(rows.length)} sub={`${num(threads.size)} lượt hỏi · ${num(askers.size)} nhân viên`} tone="blue" />
        <KpiCard label="% tìm được hàng" value={rows.length ? `${Math.round((matched / rows.length) * 100)}%` : '—'} sub={`${num(matched)} có hàng hoặc mã thay thế`} tone="green" />
        <KpiCard label="Không có / đã bán" value={num(noStock)} sub="Nhu cầu chưa có nguồn hàng" tone="red" />
        <KpiCard label="Lượt hỏi chốt/cọc" value={num(closedThreads.size)} sub={threads.size ? `${Math.round((closedThreads.size / threads.size) * 100)}% lượt hỏi` : '—'} tone="violet" />
        <KpiCard label="Đơn khớp f_sales" value={num(matchedOrders.length)} sub={matchedOrders.length ? `${mil(matchedUsedRev)} hàng cũ · ${mil(matchedTotalRev)} cả đơn` : 'Chưa có mã đơn GVN- trong thread'} tone="teal" />
        <KpiCard label="Dự án phản hồi trung vị" value={med == null ? '—' : med < 60 ? `${Math.round(med)} phút` : `${(med / 60).toFixed(1)} giờ`} sub={`${num(firstReplies.length)} / ${num(threads.size)} lượt hỏi đã được team Dự án trả lời`} tone="amber" />
      </section>

      <section className="card space">
        <div className="section-title"><h2>Lượt hỏi theo ngày &amp; câu trả lời của team Dự án</h2><span>cột = mã được hỏi · đường = thread chốt/cọc</span></div>
        <div className="chart">
          <ChartBox labels={days.map(ddmm)} datasets={datasets}
            options={{ scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } } } } as never} />
        </div>
      </section>

      <section className="grid split space atop">
        <div className="card">
          <div className="section-title"><h2>Theo mã / dòng sản phẩm</h2><span>bấm để lọc · ghi chú = số lần không có hàng</span></div>
          <RankList entries={countBy(view.model, a => a.model_text || a.product_text || '?')} selected={filters.model} onToggle={v => toggle('model', v)}
            labelFor={v => v} color="#7c3aed" limit={12} note={gapNote} />
        </div>
        <div className="card">
          <div className="section-title"><h2>Theo ngành target</h2><span>bấm để lọc</span></div>
          <RankList entries={targetEntries} selected={filters.target} onToggle={v => toggle('target', v)} labelFor={v => labelOf('target', v)} color={TARGET_COLOR.PCD} limit={8} />
          <div className="section-title space"><h2>Theo hãng</h2><span>bấm để lọc</span></div>
          <RankList entries={countBy(view.brand, a => a.brand || 'Chưa rõ')} selected={filters.brand} onToggle={v => toggle('brand', v)} labelFor={v => v} color="#0891b2" limit={8} />
        </div>
      </section>

      <section className="card space">
        <div id="sh-lark-team" className="section-title ins-anchor"><h2>Team Dự án phản hồi &amp; cập nhật</h2><span>Dũng · Sanh · Tuấn — xác định theo người gửi, không qua AI</span></div>
        <div className="scroll">
          <table className="ins-crtable">
            <thead><tr><th>Thành viên Dự án</th><th className="right">Tin phản hồi</th><th className="right">Lượt hỏi đã trả lời</th><th className="right">Trả lời đầu tiên</th><th className="right">Trung vị phản hồi đầu</th><th className="right">Tin cập nhật</th></tr></thead>
            <tbody>
              {teamRows.length === 0 ? <tr><td colSpan={6} className="muted">Chưa có phản hồi của team Dự án trong kỳ.</td></tr> : teamRows.map(r => (
                <tr key={r.name}>
                  <td>{shortName(r.name)}</td>
                  <td className="right tnum">{num(r.replies)}</td>
                  <td className="right tnum">{num(r.threads)}</td>
                  <td className="right tnum">{num(r.first)}</td>
                  <td className="right tnum">{r.med == null ? '—' : `${Math.round(r.med)} phút`}</td>
                  <td className="right tnum">{num(r.updates)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {updates.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="section-title"><h2>Cập nhật của team Dự án</h2><span>thread do team Dự án mở: danh sách seller, quy trình</span></div>
            <div style={{ display: 'grid', gap: 8 }}>
              {updates.map(m => (
                <div key={m.id} className="small" style={{ borderLeft: '3px solid var(--teal)', paddingLeft: 8 }}>
                  <span className="muted">{shortName(m.name)} · {m.t.slice(8, 10)}/{m.t.slice(5, 7)} {m.t.slice(11, 16)}</span>
                  {m.app_link ? <> · <a href={m.app_link} target="_blank" rel="noreferrer">mở LARK</a></> : null}
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="card space">
        <div className="section-title"><h2>Theo nhân viên hỏi</h2><span>doanh số hàng cũ lấy từ f_sales cùng kỳ (khóa email) · bấm tên để lọc</span></div>
        {data?.sales.error && <div className="muted">Không tải được doanh số f_sales: {data.sales.error}</div>}
        <div className="scroll">
          <table className="ins-crtable">
            <thead><tr><th>Nhân viên</th><th>Đội</th><th className="right">Lượt hỏi</th><th className="right">Mã hỏi</th><th className="right">Tìm được</th><th className="right">Chốt/cọc</th><th className="right">Đơn hàng cũ (f_sales)</th><th className="right">DT hàng cũ</th></tr></thead>
            <tbody>
              {staffRows.map(r => (
                <tr key={r.name} style={filters.staff.includes(r.name) ? { background: 'var(--surface-2, rgba(124,58,237,.08))' } : undefined}>
                  <td><button type="button" style={LINK_BTN} onClick={() => toggle('staff', r.name)} title={r.email || ''}>{shortName(r.name)}</button></td>
                  <td>{TEAM_LABEL[r.team] || r.team}{r.store && r.team === 'store' ? ` · ${r.store}` : ''}</td>
                  <td className="right tnum">{num(r.threads)}</td>
                  <td className="right tnum">{num(r.asks)}</td>
                  <td className="right tnum">{num(r.matched)}</td>
                  <td className="right tnum">{num(r.closed)}</td>
                  <td className="right tnum">{r.usedOrders == null ? '—' : num(r.usedOrders)}</td>
                  <td className="right tnum">{mil(r.usedRev)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card space">
        <div id="sh-lark-detail" className="section-title ins-anchor"><h2>Chi tiết từng mã được hỏi</h2><span>{num(rows.length)} dòng · bấm trạng thái để lọc · mở thread gốc trên LARK</span></div>
        <div role="group" aria-label="Trả lời" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '4px 0 10px' }}>
          {AVAIL_ORDER.map(av => {
            const n = view.avail.filter(a => a.availability === av).length;
            return n ? (
              <button key={av} type="button" className={`tag ${AVAIL_TONE[av]}`} style={{ cursor: 'pointer', border: 0, outline: filters.avail.includes(av) ? '2px solid currentColor' : 'none' }} onClick={() => toggle('avail', av)} aria-pressed={filters.avail.includes(av)}>
                {AVAIL_LABEL[av]} · {n}
              </button>
            ) : null;
          })}
        </div>
        <div className="scroll">
          <table className="ins-detail">
            <thead><tr><th>Ngày</th><th>Nhân viên</th><th>Sản phẩm hỏi</th><th>Hãng · nhóm</th><th>Loại</th><th>Trả lời</th><th className="right">Giá báo</th><th>Kết quả</th><th>Raw (Sales hỏi · Dự án trả lời)</th><th /></tr></thead>
            <tbody>
              {rows.slice(0, limit).map(a => (
                <tr key={a.ask_id}>
                  <td className="nowrap">{ddmm(a.day)} {hhmm(a.asked_at)}</td>
                  <td>{shortName(a.asker_name)}<div className="muted small">{TEAM_LABEL[a.asker_team || 'other']}{a.first_reply_minutes != null ? ` · trả lời sau ${Math.round(Number(a.first_reply_minutes))}′` : ''}</div></td>
                  <td>{a.product_text || '—'}{a.model_text && a.model_text !== a.product_text ? <div className="muted small">{a.model_text}</div> : null}</td>
                  <td>{a.brand || '—'}<div className="muted small">{a.ai_subcategory || a.ai_category || ''}</div></td>
                  <td>{TYPE_LABEL[a.ask_type] || a.ask_type}</td>
                  <td><Tag tone={AVAIL_TONE[a.availability] || 'gray'}>{AVAIL_LABEL[a.availability] || a.availability}</Tag></td>
                  <td className="right tnum">{mil(a.quoted_price)}</td>
                  <td><Tag tone={OUTCOME_TONE[a.outcome] || 'gray'}>{OUTCOME_LABEL[a.outcome] || a.outcome}</Tag>{a.order_codes.length ? <div className="muted small">{a.order_codes.join(', ')}</div> : null}</td>
                  <td title={a.summary || ''}><RawThread msgs={rawByRoot.get(a.root_message_id) || []} /></td>
                  <td>{a.app_link ? <a href={a.app_link} target="_blank" rel="noreferrer" title="Mở thread trên LARK"><ExternalLink size={14} /></a> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > limit && <button type="button" className="ins-more" onClick={() => setLimit(l => l + 60)}>Xem thêm {num(Math.min(60, rows.length - limit))} dòng</button>}
      </section>
    </>
  );
}
